/**
 * ValidationSection - Deck validation status display
 *
 * Displays validation errors and warnings for a deck,
 * with format-specific feedback.
 */
import type { ReactElement } from 'react';

import type { DeckFormat, DeckValidation } from '@/types/deck';

export interface ValidationSectionProps {
  /** Validation result from validateDeck */
  validation: DeckValidation;
  /** Currently selected format (null if no format selected) */
  format: DeckFormat | null;
}

/**
 * Derives the section status from validation result.
 * Used to show status indicator when section is collapsed.
 */
export function getValidationStatus(validation: DeckValidation, format: DeckFormat | null): 'valid' | 'warning' | 'error' | 'neutral' {
  // No format selected - neutral state
  if (!format) {
    return 'neutral';
  }

  // Check for unresolved errors
  const unresolvedErrors = validation.errors.filter((e) => !e.is_overridden);
  if (unresolvedErrors.length > 0) {
    return 'error';
  }

  // Check for warnings
  if (validation.warnings.length > 0) {
    return 'warning';
  }

  // All good
  return 'valid';
}

export default function ValidationSection({ validation, format }: ValidationSectionProps): ReactElement {
  const unresolvedErrors = validation.errors.filter((e) => !e.is_overridden);
  const hasIssues = unresolvedErrors.length > 0 || validation.warnings.length > 0;

  // No format selected
  if (!format) {
    return (
      <div className="rounded-lg bg-zinc-800/30 p-4 text-center">
        <div className="text-sm text-zinc-400">No format selected</div>
        <p className="mt-1 text-xs text-zinc-500">Select a format to see validation</p>
      </div>
    );
  }

  // All valid, no issues
  if (!hasIssues) {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
        <div className="text-sm font-medium text-emerald-400">{validation.is_valid ? '✓ Deck is valid' : '✓ All issues resolved'}</div>
      </div>
    );
  }

  // Has issues - display them
  return (
    <div className="space-y-2">
      {unresolvedErrors.map((issue, i) => (
        <div key={`error-${i}`} className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
          <div className="text-sm font-medium text-red-400">{issue.message}</div>
          {issue.suggested_action && <p className="mt-1 text-xs text-red-300/70">{issue.suggested_action}</p>}
        </div>
      ))}
      {validation.warnings.map((issue, i) => (
        <div key={`warning-${i}`} className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
          <div className="text-sm font-medium text-amber-400">{issue.message}</div>
          {issue.suggested_action && <p className="mt-1 text-xs text-amber-300/70">{issue.suggested_action}</p>}
        </div>
      ))}
    </div>
  );
}
